import React, { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { styled } from 'styled-components';
import { GoogleAuthProvider, signInWithCredential } from 'firebase/auth';

import useLocalStorage from 'Common/hooks/useLocalStorage';
import BlueSpinner from 'Common/components/BlueSpinner';
import { regenerateApi } from 'Common/api/DazzleApi';
import { getAuthToken, setAuthToken } from 'Common/util/Auth';
import { getTokensFromOAuthToken } from 'Common/util/Google';

import { auth } from 'Compass/api/Firebase';
import { refreshAccessToken, setRefreshToken } from 'Common/api/DazzleInternalService';

declare const COMPASS_REDIRECT_URL: string;
declare const BASE_GOOGLE_OAUTH_URL: string;

interface IUUserContext {
  name: string;

  signOut: () => void;
}

const UserContext = createContext<IUUserContext | null>(null);

export function UserContextProvider(props: { children: React.ReactNode }) {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [error, setError] = useState<ReactNode>();
  const [redirectPath, setRedirectPath, clearRedirectPath] = useLocalStorage<string | undefined>('redirectPath', undefined);
  const navigate = useNavigate();

  const [name, setName] = useState(() => {
    const [, payloadSegment] = getAuthToken().split('.');
    try {
      const payload = JSON.parse(atob(payloadSegment));
      if (payload.exp * 1000 > Date.now()) {
        return payload.email as string;
      }
      return '';
    } catch {
      return '';
    }
  });

  const finishLogin = useCallback(async () => {
    setIsLoggedIn(true);

    if (redirectPath) {
      clearRedirectPath();
      navigate(redirectPath || '/', { replace: true });
    }
  }, [clearRedirectPath, navigate, redirectPath]);

  const loggedInToFirebase = useRef(false);
  const loginToFirebase = useCallback(async (googleOAuthToken: string) => {
    if (loggedInToFirebase.current) {
      return;
    }
    loggedInToFirebase.current = true;
    const credential = GoogleAuthProvider.credential(googleOAuthToken);
    await signInWithCredential(auth, credential);

    await finishLogin();
  }, [finishLogin]);

  const checkToken = (): boolean => {
    const [, payloadSegment] = getAuthToken().split('.');

    try {
      const payload = JSON.parse(atob(payloadSegment));
      return payload.exp * 1000 > Date.now();
    } catch {
      return false;
    }
  }

  const getNewAccessToken = useCallback(async () => {
    const response = await refreshAccessToken();
    const newAccessToken = response.access_token;
    if (newAccessToken) {
      const [, payloadSegment] = newAccessToken.split('.');
      const payload = JSON.parse(atob(payloadSegment));
      setName(payload.email);

      setAuthToken(newAccessToken);
      regenerateApi();
      await loginToFirebase(newAccessToken);
    } else {
      setRedirectPath(window.location.pathname + window.location.search);
      window.location.replace(BASE_GOOGLE_OAUTH_URL + COMPASS_REDIRECT_URL);
    }
  }, [loginToFirebase, setRedirectPath]);

  const storeUser = useCallback(async (googleOAuthToken: string) => {
    try {
      const { id_token, refresh_token, email } = await getTokensFromOAuthToken(googleOAuthToken);

      setAuthToken(id_token);
      setName(email);
      if (refresh_token) {
        await setRefreshToken(refresh_token);
      }
      regenerateApi();

      await loginToFirebase(id_token);
    } catch (error) {
      setError(error.message);
    }
  }, [loginToFirebase]);

  useEffect(() => {
    const init = async () => {
      if (!checkToken()) {
        let googleOAuthToken;
        if (window.location.pathname === '/oauth/callback') {
          const url = new URL(
            window.location.href
          );

          googleOAuthToken = url.searchParams.get('code');
        }

        if (googleOAuthToken) {
          await storeUser(googleOAuthToken);
        } else {
          await getNewAccessToken();
        }
      } else {
        await loginToFirebase(getAuthToken());
      }
    };
    void init();
  }, [getNewAccessToken, loginToFirebase, storeUser]);

  const signOut = () => {
    clearRedirectPath();

    navigate('/');
    window.location.reload();
  };

  const context: IUUserContext = {
    name,

    signOut
  };

  let content: ReactNode;
  if (isLoggedIn) {
    content = props.children;
  } else if (error !== undefined) {
    content = <div>
      <h1>Login Error</h1>
      <div>{error}</div>
    </div>;
  } else {
    content = <Centered>
      <BlueSpinner/>
    </Centered>;
  }

  return (
    <UserContext.Provider value={context}>
      {content}
    </UserContext.Provider>
  );
}

const Centered = styled.div`
  width: 100vw;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const useUserContext = () => {
  const context = useContext(UserContext);
  if (context === null) {
    throw new Error('useUserContext must be used within a UserContextProvider');
  }
  return context;
};
